import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

/* ============================
   Async Thunks
============================ */

// Chat list
export const getChatList = createAsyncThunk(
  "chat/getChatList",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `admin/chat-list?search=${data?.search || ""}&filter=${data?.filter || ""}&perPage=${data?.perPage || 10}&page=${data?.page || 1}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Chat messages
export const getChatMessages = createAsyncThunk(
  "chat/getChatMessages",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `admin/chat-details/${data?.id}?search=${data?.search || ""}&filter=${data?.filter || ""}&perPage=${data?.perPage || 10}&page=${data?.page || 1}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Send message
export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/send-message", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Get member list
export const getMemberList = createAsyncThunk(
  "chat/getMemberList",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `admin/active-users?page=${data?.page || 1}&search=${data?.search || ""}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Create group
export const createGroup = createAsyncThunk(
  "chat/createGroup",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/create-group", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Get group details
export const getGroupDetails = createAsyncThunk(
  "chat/getGroupDetails",
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/group-details/${groupId}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Update group
export const updateGroup = createAsyncThunk(
  "chat/updateGroup",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/update-group", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Add group members
export const addGroupMembers = createAsyncThunk(
  "chat/addGroupMembers",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/add-group-member", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Remove group member
export const removeGroupMember = createAsyncThunk(
  "chat/removeGroupMember",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/remove-group-member", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Delete group (NEW)
export const deleteGroup = createAsyncThunk(
  "chat/deleteGroup",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/delete-group/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const offlineUser = createAsyncThunk(
  "chat/offlineUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post(`admin/offline-user`, {});
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

/* ============================
   Slice
============================ */

const chatSlice = createSlice({
  name: "chat",
  initialState: {
    chat_list: [],
    chat_list_pagination: {},

    chat_messages: [],

    member_list: [],
    member_list_pagination: {},

    group_details: null,
    selectedChat: null,

    chat_list_loading: false,
    chat_message_loading: false,
    member_list_loading: false,
    create_group_loading: false,
    group_details_loading: false,
    update_group_loading: false,
    delete_group_loading: false, // Added state
    loading: false,

    status: "idle",
  },

  reducers: {
    setSelectedChat(state, action) {
      state.selectedChat = action.payload;
    },

    unSetSelectedChat(state) {
      state.selectedChat = null;
    },

    setEmptyMessages(state, action) {
      state.chat_messages = action.payload;
    },

    updateSelectedChatGroup(state, action) {
           if (!state.selectedChat?.chat_with) return;
      state.selectedChat.chat_with = {
        ...state.selectedChat.chat_with,
        ...action.payload,
      };
    },

    // ✅ Used by socket to reorder chat list
    updateChatList(state, action) {
      state.chat_list = action.payload;
    },

    markConversationAsRead(state, action) {
      const conversationId = action.payload;
      if (!conversationId || !Array.isArray(state.chat_list)) return;
      const index = state.chat_list.findIndex(
        (c) => Number(c.conversation_id) === Number(conversationId)
      );
      if (index !== -1) {
        state.chat_list[index] = {
          ...state.chat_list[index],
          unread_count: 0,
        };
      }
    },

    resetStatus(state) {
      state.loading = false;
      state.chat_list_loading = false;
      state.chat_message_loading = false;
      state.member_list_loading = false;
      state.create_group_loading = false;
      state.group_details_loading = false;
      state.update_group_loading = false;
      state.delete_group_loading = false; // Reset added state
    },
  },

  extraReducers: (builder) => {
    builder
      /* Chat list */
      .addCase(getChatList.pending, (state) => {
        state.chat_list_loading = true;
      })
      .addCase(getChatList.fulfilled, (state, action) => {
        state.chat_list_loading = false;

        if (action.payload.pagination.current_page === 1) {
          state.chat_list = action.payload.chatList;
        } else {
          // ✅ FIX: no nested arrays
          state.chat_list.push(...action.payload.chatList);
        }

        state.chat_list_pagination = action.payload.pagination;
      })
      .addCase(getChatList.rejected, (state) => {
        state.chat_list_loading = false;
      })

      /* Chat messages */
      .addCase(getChatMessages.pending, (state) => {
        state.chat_message_loading = true;
      })
      .addCase(getChatMessages.fulfilled, (state, action) => {
        state.chat_message_loading = false;
        state.chat_messages = action.payload;
      })
      .addCase(getChatMessages.rejected, (state) => {
        state.chat_message_loading = false;
      })

      /* Member list */
      .addCase(getMemberList.pending, (state) => {
        state.member_list_loading = true;
      })
      .addCase(getMemberList.fulfilled, (state, action) => {
        state.member_list_loading = false;

        if (action.payload.pagination.current_page === 1) {
          state.member_list = action.payload.users;
        } else {
          // ✅ FIX
          state.member_list.push(...action.payload.users);
        }

        state.member_list_pagination = action.payload.pagination;
      })
      .addCase(getMemberList.rejected, (state) => {
        state.member_list_loading = false;
      })

      /* Send message */
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendMessage.rejected, (state) => {
        state.loading = false;
      })

      /* Create group */
      .addCase(createGroup.pending, (state) => {
        state.create_group_loading = true;
      })
      .addCase(createGroup.fulfilled, (state) => {
        state.create_group_loading = false;
      })
      .addCase(createGroup.rejected, (state) => {
        state.create_group_loading = false;
      })

      /* Group details */
      .addCase(getGroupDetails.pending, (state) => {
        state.group_details_loading = true;
      })
      .addCase(getGroupDetails.fulfilled, (state, action) => {
        state.group_details_loading = false;
        state.group_details = action.payload;
      })
      .addCase(getGroupDetails.rejected, (state) => {
        state.group_details_loading = false;
      })

      /* Update group */
      .addCase(updateGroup.pending, (state) => {
        state.update_group_loading = true;
      })
      .addCase(updateGroup.fulfilled, (state) => {
        state.update_group_loading = false;
      })
      .addCase(updateGroup.rejected, (state) => {
        state.update_group_loading = false;
      })

      /* Add group members */
      .addCase(addGroupMembers.pending, (state) => {
        state.loading = true;
      })
      .addCase(addGroupMembers.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addGroupMembers.rejected, (state) => {
        state.loading = false;
      })

      /* Remove group member */
      .addCase(removeGroupMember.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeGroupMember.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeGroupMember.rejected, (state) => {
        state.loading = false;
      })

      /* Delete group (NEW) */
      .addCase(deleteGroup.pending, (state) => {
        state.delete_group_loading = true;
      })
      .addCase(deleteGroup.fulfilled, (state) => {
        state.delete_group_loading = false;
      })
      .addCase(deleteGroup.rejected, (state) => {
        state.delete_group_loading = false;
      })
      .addCase(offlineUser.fulfilled, (state) => {
        console.log(state.payload);
      });
  },
});

export const {
  setSelectedChat,
  unSetSelectedChat,
  resetStatus,
  setEmptyMessages,
  updateSelectedChatGroup,
  updateChatList,
  markConversationAsRead,
} = chatSlice.actions;

export default chatSlice.reducer;